import { UUID_REGEX } from "@bindery/shared/auth/authConstants";
import { GATE_PARAM, VIA_BOOKMARK, VIA_PARAM } from "@/contracts/session";
import { storageKey } from "@/site";

export const UNCONFIRMED_READER_KEY = storageKey("unconfirmedReader.v1");
export const MAX_UNCONFIRMED_READERS = 10;

export interface UnconfirmedReader {
  readerId: string;
  markedAt: number;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const browserStorage = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

/** The reader a bookmark landing URL names, if it is one. */
export function landingGate(search: string | URLSearchParams): string | null {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  if (params.get(VIA_PARAM) !== VIA_BOOKMARK) return null;
  const gate = params.get(GATE_PARAM);
  return gate !== null && UUID_REGEX.test(gate) ? gate : null;
}

function isMarker(value: unknown): value is UnconfirmedReader {
  return (
    typeof value === "object" &&
    value !== null &&
    "readerId" in value &&
    typeof value.readerId === "string" &&
    "markedAt" in value &&
    typeof value.markedAt === "number"
  );
}

/** Every reader still awaiting "That's me"; a lone marker is the old shape. */
export function unconfirmedReaders(
  storage: Store | null = browserStorage(),
): UnconfirmedReader[] {
  const raw = storage?.getItem(UNCONFIRMED_READER_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isMarker(parsed)) return [parsed];
    if (Array.isArray(parsed) && parsed.every(isMarker)) return parsed;
  } catch {
    // Unparseable: treated as no marker, and cleared below.
  }
  storage?.removeItem(UNCONFIRMED_READER_KEY);
  return [];
}

function store(markers: UnconfirmedReader[], storage: Store | null): void {
  if (markers.length === 0) storage?.removeItem(UNCONFIRMED_READER_KEY);
  else storage?.setItem(UNCONFIRMED_READER_KEY, JSON.stringify(markers));
}

/**
 * A bookmark scan signs in by printed readerId alone, so the reader it names
 * stays unconfirmed, across reloads and tabs, until someone says "That's me".
 * Markers accumulate rather than replace: a stale landing (Back, an old tab)
 * can add a reader to confirm, never lift another reader's gate.
 */
export function markUnconfirmed(
  readerId: string,
  now: number,
  storage: Store | null = browserStorage(),
): void {
  const others = unconfirmedReaders(storage).filter(
    (marker) => marker.readerId !== readerId,
  );
  store(
    [...others, { readerId, markedAt: now }].slice(-MAX_UNCONFIRMED_READERS),
    storage,
  );
}

export function isUnconfirmed(
  readerId: string,
  storage: Store | null = browserStorage(),
): boolean {
  return unconfirmedReaders(storage).some(
    (marker) => marker.readerId === readerId,
  );
}

export function clearUnconfirmed(
  readerId: string,
  storage: Store | null = browserStorage(),
): void {
  store(
    unconfirmedReaders(storage).filter(
      (marker) => marker.readerId !== readerId,
    ),
    storage,
  );
}
